// Imperative drawing engine. Owns the stroke + redo lists, the active
// stroke during a pointer drag, the lastTool / lastActionAt activity
// signals, and the canvas-painting work. Lives outside React; the hook
// (useDrawing) handles the React-side glue.
//
// Inputs that React owns (tool, palette, colorIndex, size) flow in via
// setInputs() — the engine reads its own copy when handlers fire so a
// mid-stroke palette change behaves consistently. The canvas reference
// is passed at construction; the engine reads .current on demand so the
// element doesn't have to exist at construction time.
//
// Snapshot is event-frequency: emits on stroke commit / undo / redo /
// clear. The hook subscribes and pushes the snapshot into React state
// for things downstream (DrawingToolbar's enable/disable, scoring's
// strokes input, the speedrun activity signals).
import { redraw, renderPenStroke } from "./strokeRender";

const MIN_SIZE = 2;
const MAX_SIZE = 80;
const FILL_TOLERANCE = 24; // squared Euclidean RGB+alpha distance

function clampSize(n) {
  return Math.max(MIN_SIZE, Math.min(MAX_SIZE, n));
}

function canvasPointFromEvent(canvas, event) {
  // rect.width/height include CSS transforms (like FitContainer's scale),
  // which offsetWidth/offsetHeight do not. The visual size is what we need
  // to map viewport-pixel deltas back to canvas-buffer pixels. The drawing
  // canvas is always axis-aligned regardless of egg rotation, so a
  // straight screen→virtual map suffices.
  const rect = canvas.getBoundingClientRect();
  const displayedW = rect.width || canvas.offsetWidth;
  const displayedH = rect.height || canvas.offsetHeight;
  return {
    x: ((event.clientX - rect.left) * canvas.width) / displayedW,
    y: ((event.clientY - rect.top) * canvas.height) / displayedH,
    pressure:
      event.pressure > 0 && event.pressure !== 0.5 ? event.pressure : null,
  };
}

function floodFillMask(imgData, startX, startY, width, height, silhouette) {
  const { data } = imgData;
  const startIdx = (startY * width + startX) * 4;
  const t0 = data[startIdx];
  const t1 = data[startIdx + 1];
  const t2 = data[startIdx + 2];
  const t3 = data[startIdx + 3];
  // Egg silhouette acts as a wall: a fill seeded outside the egg can only
  // grow into other off-egg pixels, and likewise inside-egg fills stay
  // inside. Lets the player draw two strokes to the egg's left and right
  // edges and have the fill bridge across as if the egg were a closed
  // boundary.
  const seedOnEgg = silhouette ? silhouette[startY * width + startX] : 0;

  const tol2 = FILL_TOLERANCE * FILL_TOLERANCE;
  const mask = new Uint8Array(width * height);
  const stack = [startX | 0, startY | 0];

  while (stack.length > 0) {
    const y = stack.pop();
    const x = stack.pop();
    if (x < 0 || x >= width || y < 0 || y >= height) continue;
    const mi = y * width + x;
    if (mask[mi]) continue;
    if (silhouette && silhouette[mi] !== seedOnEgg) continue;
    const pi = mi * 4;
    const dr = data[pi] - t0;
    const dg = data[pi + 1] - t1;
    const db = data[pi + 2] - t2;
    const da = data[pi + 3] - t3;
    if (dr * dr + dg * dg + db * db + da * da > tol2) continue;
    mask[mi] = 1;
    stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
  }

  return mask;
}

function dilateMask(mask, width, height, iterations = 1) {
  let cur = mask;
  for (let k = 0; k < iterations; k++) {
    const next = new Uint8Array(cur.length);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = y * width + x;
        if (cur[i]) {
          next[i] = 1;
          if (x > 0) next[i - 1] = 1;
          if (x < width - 1) next[i + 1] = 1;
          if (y > 0) next[i - width] = 1;
          if (y < height - 1) next[i + width] = 1;
        }
      }
    }
    cur = next;
  }
  return cur;
}

function cropMaskToBbox(mask, width, height) {
  let minX = width,
    minY = height,
    maxX = -1,
    maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (mask[y * width + x]) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) {
    return { bbox: { x: 0, y: 0, w: 0, h: 0 }, mask: new Uint8Array(0) };
  }
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const cropped = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      cropped[y * w + x] = mask[(y + minY) * width + (x + minX)];
    }
  }
  return { bbox: { x: minX, y: minY, w, h }, mask: cropped };
}

export default class DrawingEngine {
  constructor({ canvasRef, eggSilhouetteRef }) {
    this.canvasRef = canvasRef;
    this.eggSilhouetteRef = eggSilhouetteRef;

    this.strokes = [];
    this.redoStack = [];
    this.activeStroke = null;
    this.activePointerId = null;
    this.lastTool = null;
    this.lastActionAt = 0;

    // Inputs from React, refreshed via setInputs.
    this.tool = "pen";
    this.palette = [];
    this.colorIndex = 0;
    this.size = 16;
    // When true, committed strokes have colorIndex nulled so palette
    // slot edits no longer recolor them — creative mode wants new
    // strokes frozen at draw-time. Default false matches author/speedrun.
    this.freezeStrokeColor = false;

    this.listeners = new Set();
  }

  setInputs({ tool, palette, colorIndex, size, freezeStrokeColor }) {
    const paletteChanged = palette !== this.palette;
    this.tool = tool;
    this.palette = palette;
    this.colorIndex = colorIndex;
    this.size = size;
    this.freezeStrokeColor = !!freezeStrokeColor;
    if (paletteChanged) this._redrawAll();
  }

  onPointerDown(event) {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    if (event.button !== 0 && event.pointerType === "mouse") return;

    const point = canvasPointFromEvent(canvas, event);

    if (this.tool === "fill") {
      this._applyFillAt(point.x, point.y);
      return;
    }

    canvas.setPointerCapture(event.pointerId);
    this.activePointerId = event.pointerId;

    const stroke = {
      tool: this.tool,
      colorIndex: this.tool === "eraser" ? null : this.colorIndex,
      color: this.tool === "eraser" ? null : this.palette[this.colorIndex],
      size: clampSize(this.size),
      points: [point],
    };
    this.activeStroke = stroke;
    const ctx = canvas.getContext("2d");
    renderPenStroke(ctx, stroke, this.palette);
  }

  onPointerMove(event) {
    if (this.activePointerId !== event.pointerId) return;
    const stroke = this.activeStroke;
    if (!stroke) return;
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const point = canvasPointFromEvent(canvas, event);
    const prev = stroke.points[stroke.points.length - 1];
    const dx = point.x - prev.x;
    const dy = point.y - prev.y;
    if (dx * dx + dy * dy < 0.5) return;
    stroke.points.push(point);
    const ctx = canvas.getContext("2d");
    renderPenStroke(
      ctx,
      { ...stroke, points: [prev, point] },
      this.palette,
    );
  }

  onPointerUp(event) {
    if (this.activePointerId !== event.pointerId) return;
    const stroke = this.activeStroke;
    this.activeStroke = null;
    this.activePointerId = null;
    if (!stroke) return;
    this._commit(stroke);
  }

  undo() {
    if (this.strokes.length === 0) return;
    const popped = this.strokes[this.strokes.length - 1];
    this.strokes = this.strokes.slice(0, -1);
    this.redoStack = [...this.redoStack, popped];
    this._redrawAll();
    this._emit();
  }

  redo() {
    if (this.redoStack.length === 0) return;
    const popped = this.redoStack[this.redoStack.length - 1];
    this.redoStack = this.redoStack.slice(0, -1);
    this.strokes = [...this.strokes, popped];
    this._redrawAll();
    this._emit();
  }

  // Wipe all strokes + redo history and repaint. Used between marcs in
  // a speedrun round. Does not touch tool/palette/size inputs — those
  // belong to React.
  clear() {
    if (this.strokes.length === 0 && this.redoStack.length === 0) return;
    this.strokes = [];
    this.redoStack = [];
    this._redrawAll();
    this._emit();
  }

  getSnapshot() {
    return {
      strokes: this.strokes,
      canUndo: this.strokes.length > 0,
      canRedo: this.redoStack.length > 0,
      lastTool: this.lastTool,
      lastActionAt: this.lastActionAt,
    };
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispose() {
    this.listeners.clear();
    this.activeStroke = null;
    this.activePointerId = null;
  }

  _commit(stroke) {
    if (this.freezeStrokeColor && stroke.colorIndex != null) {
      stroke.colorIndex = null;
    }
    this.strokes = [...this.strokes, stroke];
    this.redoStack = [];
    this.lastTool = stroke.tool;
    this.lastActionAt = Date.now();
    this._emit();
  }

  _applyFillAt(canvasX, canvasY) {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const x = Math.max(0, Math.min(canvas.width - 1, Math.round(canvasX)));
    const y = Math.max(0, Math.min(canvas.height - 1, Math.round(canvasY)));
    const silhouette = this.eggSilhouetteRef?.current?.data ?? null;
    const rawMask = floodFillMask(
      imgData,
      x,
      y,
      canvas.width,
      canvas.height,
      silhouette,
    );
    // Dilate by 2px to cover anti-aliased stroke edges — without this
    // the fill stops one pixel short of opaque stroke cores and leaves
    // a halo.
    const mask = dilateMask(rawMask, canvas.width, canvas.height, 2);
    // Dilation doesn't know about the silhouette wall, so it can extend
    // the fill up to 2px across the egg boundary. Clip back to the
    // seed's silhouette state.
    if (silhouette) {
      const seedOnEgg = silhouette[y * canvas.width + x];
      for (let i = 0; i < mask.length; i++) {
        if (mask[i] && silhouette[i] !== seedOnEgg) mask[i] = 0;
      }
    }
    const { bbox, mask: cropped } = cropMaskToBbox(
      mask,
      canvas.width,
      canvas.height,
    );
    if (cropped.length === 0) return;
    const fill = {
      tool: "fill",
      colorIndex: this.colorIndex,
      color: this.palette[this.colorIndex],
      bbox,
      mask: cropped,
    };
    // Render the fill to canvas immediately so subsequent fills see
    // updated pixels (otherwise the next fill's flood would seed off
    // the pre-fill canvas state).
    this._commit(fill);
    this._redrawAll();
  }

  _redrawAll() {
    const canvas = this.canvasRef?.current;
    if (!canvas) return;
    redraw(canvas, this.strokes, this.palette);
  }

  _emit() {
    for (const l of this.listeners) l();
  }
}
